import { get, post, put, patch, del, download, uploadFile } from './client';

/**
 * Every admin API call, in one place.
 *
 * Grouped by module and named for what the operator is doing, not for the HTTP
 * verb. Each returns the unwrapped payload (or throws an ApiError) so react-query
 * hooks stay one-liners.
 */

const B = '/api/admin';

export const auth = {
  login: (email, password) => post(`${B}/auth/login`, { email, password }),
  verifyMfa: (mfaToken, code) => post(`${B}/auth/login/verify`, { mfaToken, code }),
  enrollMfa: (mfaToken, code) => post(`${B}/auth/mfa/enroll`, { mfaToken, code }),
  me: () => get(`${B}/auth/me`),
  logout: () => post(`${B}/auth/logout`),
  regenerateRecoveryCodes: (code) => post(`${B}/auth/mfa/recovery-codes`, { code }),
};

export const metrics = {
  overview: (params) => get(`${B}/metrics/overview`, params),
  timeseries: (params) => get(`${B}/metrics/timeseries`, params),
  leaderboard: (params) => get(`${B}/metrics/leaderboard`, params),
  revenue: (params) => get(`${B}/metrics/revenue`, params),
  // Quotations, invoices and marketplace money, grouped by currency.
  financials: (params) => get(`${B}/metrics/financials`, params),
};

export const organizations = {
  list: (params) => get(`${B}/organizations`, params),
  meta: () => get(`${B}/organizations/meta`),
  detail: (id) => get(`${B}/organizations/${id}`),
  // One row per tenant with every headline count side by side.
  stats: (params) => get(`${B}/organizations/stats`, params),
  statsCsv: (params) =>
    download(`${B}/organizations/stats`, { ...params, format: 'csv', limit: 500 }, 'tenant-stats.csv'),
  // The tenant 360.
  analytics: (id, params) => get(`${B}/organizations/${id}/analytics`, params),
  update: (id, body) => patch(`${B}/organizations/${id}`, body),
  suspend: (id, reason) => post(`${B}/organizations/${id}/suspend`, { reason }),
  reactivate: (id, reason) => post(`${B}/organizations/${id}/reactivate`, { reason }),
  regenerateCode: (id) => post(`${B}/organizations/${id}/regenerate-code`),
  exportTenant: (id, format = 'json') =>
    download(`${B}/organizations/${id}/export`, { format }, `tenant-export.${format}`),
  // Soft by default. `hard` additionally requires the typed name.
  remove: (id, { hard = false, confirmName, reason } = {}) =>
    del(`${B}/organizations/${id}?hard=${hard}`, { data: { confirmName, reason } }),
};

export const users = {
  // A field supervisor cannot record or route anything until they are pinned
  // to a market. Superadmins are deliberately unrestricted.
  setMarket: (id, countryCode) => post(`${B}/users/${id}/market`, { countryCode }),
  // A platform role, so it is minted here rather than through the role editor.
  // The market is required: without one they cannot record or route anything.
  createFieldSupervisor: (body) => post(`${B}/users/field-supervisor`, body),
  // Same shape, for the distributor channel — a distributor account cannot
  // act until it is linked to a Distributor record.
  setDistributor: (id, distributorId) => post(`${B}/users/${id}/distributor`, { distributorId }),
  createDistributor: (body) => post(`${B}/users/distributor`, body),
  list: (params) => get(`${B}/users`, params),
  meta: () => get(`${B}/users/meta`),
  detail: (id) => get(`${B}/users/${id}`),
  update: (id, body) => patch(`${B}/users/${id}`, body),
  resetPassword: (id, password) => post(`${B}/users/${id}/reset-password`, { password }),
  disable: (id, reason) => post(`${B}/users/${id}/disable`, { reason }),
  enable: (id) => post(`${B}/users/${id}/enable`),
  remove: (id) => del(`${B}/users/${id}`),
  createSuperadmin: (body) => post(`${B}/users/superadmin`, body),
};

/** The seven read-only cross-tenant explorers share one shape. */
const explorer = (name) => ({
  list: (params) => get(`${B}/operations/${name}`, params),
  exportCsv: (params) =>
    download(`${B}/operations/${name}`, { ...params, format: 'csv', limit: 200 }, `${name}.csv`),
});

export const operations = {
  jobcards: explorer('jobcards'),
  bookings: explorer('bookings'),
  customers: explorer('customers'),
  vehicles: explorer('vehicles'),
  inquiries: explorer('inquiries'),
  bays: explorer('bays'),
  services: explorer('services'),
};

export const technicians = {
  list: (params) => get(`${B}/technicians`, params),
  detail: (id) => get(`${B}/technicians/${id}`),
  update: (id, body) => patch(`${B}/technicians/${id}`, body),
  // commentOnly clears the text and keeps the score.
  moderateRating: (id, ratingId, commentOnly = false) =>
    del(`${B}/technicians/${id}/ratings/${ratingId}?commentOnly=${commentOnly}`),
  remove: (id) => del(`${B}/technicians/${id}`),
};

export const feedback = {
  list: (params) => get(`${B}/feedback`, params),
  summary: () => get(`${B}/feedback/summary`),
  detail: (id) => get(`${B}/feedback/${id}`),
  triage: (id, body) => patch(`${B}/feedback/${id}`, body),
  exportCsv: (params) => download(`${B}/feedback`, { ...params, format: 'csv', limit: 200 }, 'feedback.csv'),
};

export const mail = {
  garages: (params) => get(`${B}/mail/garages`, params),
  templates: () => get(`${B}/mail/templates`),
  config: () => get(`${B}/mail/config`),
  preview: (body) => post(`${B}/mail/preview`, body),
  resolveRecipients: (body) => post(`${B}/mail/resolve-recipients`, body),
  send: (body) => post(`${B}/mail/send`, body),
  history: (params) => get(`${B}/mail/history`, params),
  historyDetail: (id) => get(`${B}/mail/history/${id}`),

  // Opt-out list. Recipients add themselves through the public /u/ page; these
  // are the operator's view of the same list, plus the ability to add or remove
  // an address by hand.
  optOuts: (params) => get(`${B}/mail/optouts`, params),
  addOptOut: (body) => post(`${B}/mail/optouts`, body),
  removeOptOut: (email) => del(`${B}/mail/optouts/${encodeURIComponent(email)}`),
};

/**
 * Incoming Mail — the inbox for everything sent to the domain.
 *
 * Every one of these is an authenticated call to the Node API. There is no S3
 * client in this bundle, no bucket name, no region and no credential: the
 * browser addresses a message by its database id and the server resolves that
 * to an object it alone can read. Attachments and the raw .eml come back
 * through `download()`, which streams them from the API with the admin token
 * attached — not from a presigned URL, which would be a bearer token for a
 * private object living in someone's browser history.
 */
export const incomingMail = {
  list: (params) => get(`${B}/incoming-mails`, params),
  meta: () => get(`${B}/incoming-mails/meta`),
  detail: (id) => get(`${B}/incoming-mails/${id}`),
  // What Refresh calls: walks the bucket and ingests anything not yet indexed.
  sync: () => post(`${B}/incoming-mails/sync`),
  markRead: (id, read = true) => patch(`${B}/incoming-mails/${id}/read`, { read }),
  // Answers the sender through SES, from an address on our own verified domain.
  // `to` is whatever the operator typed, so a reply can be redirected to a
  // colleague or a different address the customer gave in the body.
  reply: (id, body) => post(`${B}/incoming-mails/${id}/reply`, body),
  archive: (id, archived = true) => patch(`${B}/incoming-mails/${id}/archive`, { archived }),
  // Soft delete — the original message stays in the S3 bucket.
  remove: (id) => del(`${B}/incoming-mails/${id}`),
  downloadAttachment: (id, index, filename) =>
    download(`${B}/incoming-mails/${id}/attachments/${index}`, undefined, filename || 'attachment'),
  downloadRaw: (id) => download(`${B}/incoming-mails/${id}/raw`, undefined, `${id}.eml`),
};

/**
 * Field-supervisor inspections and the garage handoff.
 *
 * The only module a `field_supervisor` can reach. `assign` and `transfer` hit
 * different endpoints but run the same custody engine server-side — the
 * difference is only who is handing the job on.
 */
export const fieldJobs = {
  list: (params) => get(`${B}/field-jobs`, params),
  vocabulary: () => get(`${B}/field-jobs/vocabulary`),
  // Garages this operator may route to. Already market-filtered server-side.
  garages: (params) => get(`${B}/field-jobs/garages`, params),
  detail: (id) => get(`${B}/field-jobs/${id}`),
  create: (body) => post(`${B}/field-jobs`, body),
  update: (id, body) => patch(`${B}/field-jobs/${id}`, body),
  // draft -> ready. Until this, the job cannot be handed to anyone.
  markReady: (id) => post(`${B}/field-jobs/${id}/ready`),
  assign: (id, body) => post(`${B}/field-jobs/${id}/assign`, body),
  transfer: (id, body) => post(`${B}/field-jobs/${id}/transfer`, body),
  respond: (id, body) => post(`${B}/field-jobs/${id}/respond`, body),
  // Take it back from the garage holding it. Their copy of the customer,
  // vehicle and job card goes with it.
  recall: (id, reason) => post(`${B}/field-jobs/${id}/recall`, { reason }),
  cancel: (id, reason) => post(`${B}/field-jobs/${id}/cancel`, { reason }),
  // Permanent. Any garage holding it has its rows taken back first.
  remove: (id) => del(`${B}/field-jobs/${id}`),
  // Defect and condition photos. Resolves to the usual { success, data }
  // envelope; `data.url` is what gets stored on the inspection — see
  // `uploadInspectionImage` in pages/FieldJobs.jsx, which unwraps it.
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return uploadFile(`${B}/field-jobs/images`, formData);
  },
};

/**
 * Superadmin administration of the distributor channel.
 * See server/services/distributors.service.js.
 */
export const distributors = {
  list: (params) => get(`${B}/distributors`, params),
  meta: () => get(`${B}/distributors/meta`),
  detail: (id) => get(`${B}/distributors/${id}`),
  create: (body) => post(`${B}/distributors`, body),
  update: (id, body) => patch(`${B}/distributors/${id}`, body),
  ledger: (id, params) => get(`${B}/distributors/${id}/ledger`, params),
  // The console login(s) this distributor can actually sign in with.
  logins: (id) => get(`${B}/distributors/${id}/logins`),
  grantCredits: (id, amount, note) => post(`${B}/distributors/${id}/credits`, { amount, note }),
  adjustCredits: (id, delta, note) => post(`${B}/distributors/${id}/credits/adjust`, { delta, note }),
  suspend: (id, reason) => post(`${B}/distributors/${id}/suspend`, { reason }),
  reactivate: (id) => post(`${B}/distributors/${id}/reactivate`),
  // Unlike suspend, permanent — no route back. Also revokes every activation
  // code this distributor still has outstanding.
  terminate: (id, reason) => post(`${B}/distributors/${id}/terminate`, { reason }),
  proRequests: (params) => get(`${B}/distributors/pro-requests`, params),
  approveProRequest: (id, note) => post(`${B}/distributors/pro-requests/${id}/approve`, { note }),
  rejectProRequest: (id, note) => post(`${B}/distributors/pro-requests/${id}/reject`, { note }),
  // A platform role, so it is minted here rather than through the role editor
  // — same shape as users.createFieldSupervisor.
  createLogin: (body) => post(`${B}/users/distributor`, body),
};

/**
 * The distributor's own side of the channel — what a `distributor` console
 * role reaches. A superadmin may call the same functions on a distributor's
 * behalf by passing `distributorId`; a distributor account never needs to —
 * its own record is resolved server-side from the token.
 * See server/services/distributorPortal.service.js.
 */
export const distributorPortal = {
  me: (distributorId) => get(`${B}/distributor-portal/me`, distributorId ? { distributorId } : undefined),
  garages: (params) => get(`${B}/distributor-portal/garages`, params),
  createGarage: (body) => post(`${B}/distributor-portal/garages`, body),
  upgradeGarage: (id, body) => post(`${B}/distributor-portal/garages/${id}/upgrade`, body),
  ledger: (params) => get(`${B}/distributor-portal/credit-ledger`, params),
  activationCodes: (params) => get(`${B}/distributor-portal/activation-codes`, params),
  createActivationCode: (body) => post(`${B}/distributor-portal/activation-codes`, body),
  revokeActivationCode: (id, distributorId) =>
    post(`${B}/distributor-portal/activation-codes/${id}/revoke`, distributorId ? { distributorId } : undefined),
  // A distributor's own view of the Path-3 requests it submitted — whether
  // each was approved, rejected (with the reason), or is still pending.
  proRequests: (params) => get(`${B}/distributor-portal/pro-requests`, params),
};

/**
 * Path 4 — unauthenticated. The only calls in this file that never carry the
 * admin token: a garage redeeming a code at its own signup, before it has any
 * account at all. See server/routes/publicActivation.js.
 */
export const publicActivation = {
  preview: (code) => get(`${B}/public/activation/${encodeURIComponent(code)}`),
  redeem: (code, body) => post(`${B}/public/activation/${encodeURIComponent(code)}/redeem`, body),
};

export const audit = {
  list: (params) => get(`${B}/audit`, params),
  meta: () => get(`${B}/audit/meta`),
  detail: (id) => get(`${B}/audit/${id}`),
  exportCsv: (params) => download(`${B}/audit`, { ...params, format: 'csv', limit: 200 }, 'audit.csv'),
};

export const health = {
  get: () => get(`${B}/health`),
};

/**
 * The public marketing homepage's content — draft/publish/revisions/images.
 * See server/routes/landingPage.js. `blocks` is the ordered list the editor
 * drags, hides and edits; nothing here is live until `publish()`.
 */
export const landingPage = {
  get: () => get(`${B}/landing-page`),
  meta: () => get(`${B}/landing-page/meta`),
  // PUT — the draft's whole block list is replaced on every save.
  saveDraft: (blocks) => put(`${B}/landing-page/draft`, { blocks }),
  publish: () => post(`${B}/landing-page/publish`),
  listRevisions: () => get(`${B}/landing-page/revisions`),
  restoreRevision: (id) => post(`${B}/landing-page/revisions/${id}/restore`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return uploadFile(`${B}/landing-page/images`, formData);
  },
};
